import React, { useState } from 'react';
import { ArrowRight, Check, RotateCcw, HelpCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
type Step = {
  id: number;
  question: string;
  options: {
    label: string;
    value: string;
  }[];
};
const steps: Step[] = [{
  id: 1,
  question: 'What are you surfacing?',
  options: [{
    label: 'Driveway',
    value: 'driveway'
  }, {
    label: 'Walkway or Steps',
    value: 'walkway'
  }, {
    label: 'Patio',
    value: 'patio'
  }, {
    label: 'Multiple Areas',
    value: 'multiple'
  }]
}, {
  id: 2,
  question: 'What is your top priority?',
  options: [{
    label: 'Lowest Upfront Cost',
    value: 'cost'
  }, {
    label: 'Longest Lifespan',
    value: 'lifespan'
  }, {
    label: 'Best Appearance',
    value: 'appearance'
  }, {
    label: 'Easiest Maintenance',
    value: 'maintenance'
  }]
}, {
  id: 3,
  question: 'Is this new construction or replacing?',
  options: [{
    label: 'New Construction',
    value: 'new'
  }, {
    label: 'Replacing Concrete',
    value: 'concrete'
  }, {
    label: 'Replacing Asphalt',
    value: 'asphalt'
  }, {
    label: 'Replacing Pavers',
    value: 'pavers'
  }]
}, {
  id: 4,
  question: 'Budget comfort level?',
  options: [{
    label: 'Keep it practical',
    value: 'practical'
  }, {
    label: 'Mid-range is fine',
    value: 'mid'
  }, {
    label: 'I want the best',
    value: 'high'
  }]
}];
export function MaterialDecisionGuide() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResult, setShowResult] = useState(false);
  const handleOptionSelect = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentStep + 1]: value
    };
    setAnswers(newAnswers);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };
  const getRecommendation = () => {
    const surface = answers[1];
    const priority = answers[2];
    const replacing = answers[3];
    const budget = answers[4];
    if (surface === 'multiple') {
      return {
        type: 'Custom Assessment Needed',
        description: 'Since you have multiple areas, we recommend mixing materials. For example, concrete for the driveway and pavers for the patio.',
        warning: null
      };
    }
    if (replacing === 'pavers') {
      return {
        type: 'Interlock Pavers',
        description: "Since you are replacing pavers, we can likely reuse the existing base (if it's in good condition), saving you money on excavation.",
        warning: null
      };
    }
    if (surface === 'driveway' && priority === 'cost') {
      return {
        type: 'Asphalt',
        description: 'Asphalt is the most budget-friendly option for driveways. It works well with heating but requires more maintenance over time.',
        warning: 'Shorter lifespan (15-20 years) compared to concrete.'
      };
    }
    if (surface === 'driveway' && priority === 'lifespan') {
      return {
        type: 'Concrete (Poured)',
        description: "For maximum longevity, poured concrete is the winner. It's durable, low maintenance, and transfers heat efficiently.",
        warning: null
      };
    }
    if (surface === 'driveway' && priority === 'appearance') {
      return {
        type: 'Interlock Pavers',
        description: 'Pavers offer the best curb appeal and design flexibility. They are also easy to repair if needed.',
        warning: 'Requires periodic joint maintenance.'
      };
    }
    if (surface === 'walkway' && priority === 'maintenance') {
      return {
        type: 'Concrete (Broom Finish)',
        description: 'A broom finish concrete walkway is the ultimate low-maintenance, high-safety choice. Excellent traction and durability.',
        warning: null
      };
    }
    if (surface === 'patio' && priority === 'appearance' && budget === 'high') {
      return {
        type: 'Natural Stone',
        description: "For a premium patio, natural stone is unmatched. It's durable and timeless.",
        warning: 'Highest cost option.'
      };
    }
    if (surface === 'patio' && budget === 'mid') {
      return {
        type: 'Interlock Pavers',
        description: 'Pavers are perfect for patios—comfortable, beautiful, and available in many styles to match your home.',
        warning: null
      };
    }
    // Default fallback
    return {
      type: 'Concrete (Poured)',
      description: 'Concrete is our most versatile recommendation. It balances cost, durability, and performance for most heated applications.',
      warning: null
    };
  };
  const recommendation = showResult ? getRecommendation() : null;
  return <section id="material-guide" className="py-16 bg-white md:py-24" data-id="element-637">
      <div className="container mx-auto px-4 md:px-6" data-id="element-638">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-639">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-640">
            Find Your Best Material in 60 Seconds
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-641">
            Answer 4 quick questions to get a personalized recommendation.
          </p>
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-642">
          <Card className="shadow-xl border-slate-200 overflow-hidden" data-id="element-643">
            <div className="bg-slate-100 h-2 w-full" data-id="element-644">
              <div className="bg-primary h-full transition-all duration-500 ease-out" style={{
              width: `${(currentStep + 1) / steps.length * 100}%`
            }} data-id="element-645"></div>
            </div>

            <CardContent className="p-8 md:p-12" data-id="element-646">
              {!showResult ? <div className="animate-in fade-in slide-in-from-right-4 duration-300" key={currentStep} data-id="element-647">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider mb-2 block" data-id="element-648">
                    Question {currentStep + 1} of {steps.length}
                  </span>
                  <h3 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-649">
                    {steps[currentStep].question}
                  </h3>

                  <div className="grid gap-4" data-id="element-650">
                    {steps[currentStep].options.map(option => <button key={option.value} onClick={() => handleOptionSelect(option.value)} className="text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all duration-200 flex items-center justify-between group" data-id="element-651">
                        <span className="font-medium text-slate-700 group-hover:text-primary" data-id="element-652">
                          {option.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all" data-id="element-653" />
                      </button>)}
                  </div>
                </div> : <div className="text-center animate-in fade-in zoom-in-95 duration-500" data-id="element-654">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-655">
                    {recommendation?.type.includes('Custom') ? <HelpCircle className="h-8 w-8" data-id="element-656" /> : <Check className="h-8 w-8" data-id="element-657" />}
                  </div>

                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-658">
                    We Recommend
                  </h3>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-659">
                    {recommendation?.type}
                  </h2>

                  <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-660">
                    {recommendation?.description}
                  </p>

                  {recommendation?.warning && <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg mb-8 inline-block text-left" data-id="element-661">
                      <div className="flex gap-2 items-start" data-id="element-662">
                        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" data-id="element-663" />
                        <p className="text-sm text-amber-800" data-id="element-664">
                          {recommendation.warning}
                        </p>
                      </div>
                    </div>}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-665">
                    <Button size="lg" onClick={() => {
                  const element = document.getElementById('quote-form');
                  if (element) element.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} data-id="element-666">
                      Get a Quote for This Material
                    </Button>
                    <Button variant="ghost" onClick={resetQuiz} className="text-slate-500 hover:text-slate-900" data-id="element-667">
                      <RotateCcw className="h-4 w-4 mr-2" data-id="element-668" /> Start Over
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}